'use client';
import { useState, useEffect, useLayoutEffect } from 'react';

const QUERY_REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
const QUERY_REDUCED_DATA = '(prefers-reduced-data: reduce)';

const FAIL_SAFE_PREFERENCES = {
  prefersReducedMotion: true,
  prefersReducedData: true,
};

const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

const readMediaPreferences = () => {
  if (
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function'
  ) {
    return FAIL_SAFE_PREFERENCES;
  }

  const motionQuery = window.matchMedia(QUERY_REDUCED_MOTION);
  const dataQuery = window.matchMedia(QUERY_REDUCED_DATA);

  return {
    prefersReducedMotion: motionQuery.matches,
    prefersReducedData: dataQuery.matches,
  };
};

/**
 * Tracks user media preferences that affect motion and autoplay behaviour.
 * Defaults to static media until preferences are known.
 */
export const useMediaPreferences = () => {
  const [preferences, setPreferences] = useState(readMediaPreferences);

  useIsomorphicLayoutEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const motionQuery = window.matchMedia(QUERY_REDUCED_MOTION);
    const dataQuery = window.matchMedia(QUERY_REDUCED_DATA);

    const sync = () => {
      setPreferences({
        prefersReducedMotion: motionQuery.matches,
        prefersReducedData: dataQuery.matches,
      });
    };

    sync();
    motionQuery.addEventListener('change', sync);
    dataQuery.addEventListener('change', sync);

    return () => {
      motionQuery.removeEventListener('change', sync);
      dataQuery.removeEventListener('change', sync);
    };
  }, []);

  const preferStaticMedia =
    preferences.prefersReducedMotion || preferences.prefersReducedData;

  return { ...preferences, preferStaticMedia };
};
